import { createSlice, createAsyncThunk, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import apiClient from '../../api/apiClient';
import extractApiError from '../../api/extractApiError';

interface Publisher {
  id: number;
  name: string;
  display_name: string;
  slug: string;
  status: string;
  content_status: string;
}

interface AuthState {
  loading: boolean;
  token: string | null;
  error: SerializedError | string | null;
  mobile: string;
  code: string;
  ttl: number;
  length: number;
  isRegistered: boolean;
  status: string;
  publishers: Publisher[];
  recaptchaToken: string | null;
  isParent: boolean;
}

const initialState: AuthState = {
  loading: false,
  token: Cookies.get('authToken') || null,
  error: null,
  mobile: Cookies.get('userMobile') || '', // اضافه کردن mobile از cookie
  code: '',
  ttl: 120,
  length: 6,
  isRegistered: false,
  status: '',
  publishers: [],
  recaptchaToken: null,
  isParent: Cookies.get('isParent') === 'true' || false,
};

interface VerifyCodePayload {
  mobile: string;
  code: string;
}

interface VerifyCodeResponse {
  token: string;
  publishers: Publisher[];
  is_parent: boolean;
}

interface SendMobileNumberPayload {
  mobile: string;
  length: number;
  ttl: number;
}

export const sendMobileNumber = createAsyncThunk<
  SendMobileNumberPayload,
  { mobile: string; recaptchaToken: string | null }
>(
  'auth/sendMobileNumber',
  async ({ mobile, recaptchaToken }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/sendCode', {
        mobile,
        'g-recaptcha-response': recaptchaToken,
      });
      return { ...response.data, mobile };
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to send mobile number'));
    }
  }
);

export const verifyCode = createAsyncThunk<VerifyCodeResponse, VerifyCodePayload>(
  'auth/verifyCode',
  async ({ mobile, code }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/v2/publisher/authenticate', { mobile, code });
      const { token, publishers, is_parent } = response.data;
      if (token) {
        Cookies.set('authToken', token);
        Cookies.set('userMobile', mobile); // ذخیره mobile در cookie
        Cookies.set('isParent', is_parent ? 'true' : 'false'); // ذخیره isParent در cookie
        return { token, publishers, is_parent };
      } else {
        throw new Error('Token not found in response');
      }
    } catch (error: any) {
      return rejectWithValue(extractApiError(error, 'Failed to verify code'));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.error = null;
      state.mobile = '';
      state.code = '';
      state.ttl = 0;
      state.length = 0;
      state.isRegistered = false;
      state.status = '';
      state.publishers = [];
      state.recaptchaToken = null;
      state.isParent = false;
      Cookies.remove('authToken');
      Cookies.remove('userMobile'); // حذف mobile از cookie
      Cookies.remove('isParent'); // حذف isParent از cookie
    },
    setCode: (state, action: PayloadAction<string>) => {
      state.code = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsRegistered: (state, action: PayloadAction<boolean>) => {
      state.isRegistered = action.payload;
    },
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      if (action.payload) {
        Cookies.set('authToken', action.payload);
      } else {
        Cookies.remove('authToken');
      }
    },
    setRecaptchaToken: (state, action: PayloadAction<string | null>) => {
      state.recaptchaToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMobileNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMobileNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.mobile = action.payload.mobile;
        state.ttl = action.payload.ttl;
        state.length = action.payload.length;
        state.isRegistered = true;
        Cookies.set('userMobile', action.payload.mobile); // ذخیره mobile در cookie
      })
      .addCase(sendMobileNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to send mobile number';
      })
      .addCase(verifyCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyCode.fulfilled, (state, action: PayloadAction<VerifyCodeResponse>) => {
        state.loading = false;
        state.token = action.payload.token;
        state.publishers = action.payload.publishers;
        state.isParent = action.payload.is_parent;
        // mobile قبلاً در async thunk ذخیره شده است
      })
      .addCase(verifyCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Verification failed';
      });
  },
});

export const {
  logout,
  setCode,
  setLoading,
  setError,
  setIsRegistered,
  setToken,
  setRecaptchaToken,
} = authSlice.actions;
export default authSlice.reducer;